import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Save, Plus, X, Plane, MapPin, Clock } from "lucide-react";
import { format } from "date-fns";
import { logActivity } from "@/lib/activityLogger";

interface ItineraryDocument {
  id: string;
  inquiry_id: string;
  contract_number: string;
  client_name: string;
  client_email: string;
  event_details: any;
  additional_notes: string | null;
  storage_path: string | null;
  created_at: string;
}

interface ItineraryEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: ItineraryDocument;
  onSaved?: () => void;
}

interface TimelineNote {
  id: string;
  time: string;
  note: string;
}

export function ItineraryEditor({
  open,
  onOpenChange,
  document,
  onSaved,
}: ItineraryEditorProps) {
  const [saving, setSaving] = useState(false);
  const [additionalNotes, setAdditionalNotes] = useState(document.additional_notes || "");
  const [timelineNotes, setTimelineNotes] = useState<TimelineNote[]>([]);
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [hotelDetails, setHotelDetails] = useState("");
  const [groundTransport, setGroundTransport] = useState("");
  const [mealArrangements, setMealArrangements] = useState("");

  useEffect(() => {
    if (open && document) {
      // Parse existing notes to extract structured data
      const notes = document.additional_notes || "";
      
      // Try to parse structured data from additional_notes JSON
      try {
        const parsed = JSON.parse(notes);
        if (parsed.timelineNotes) setTimelineNotes(parsed.timelineNotes);
        if (parsed.specialInstructions) setSpecialInstructions(parsed.specialInstructions);
        if (parsed.hotelDetails) setHotelDetails(parsed.hotelDetails);
        if (parsed.groundTransport) setGroundTransport(parsed.groundTransport);
        if (parsed.mealArrangements) setMealArrangements(parsed.mealArrangements);
        if (parsed.generalNotes) setAdditionalNotes(parsed.generalNotes);
      } catch {
        // Not JSON, treat as plain text
        setAdditionalNotes(notes);
        setTimelineNotes([]);
        setSpecialInstructions("");
        setHotelDetails("");
        setGroundTransport("");
        setMealArrangements("");
      }
    }
  }, [open, document]);

  const addTimelineNote = () => {
    setTimelineNotes([
      ...timelineNotes,
      { id: crypto.randomUUID(), time: "", note: "" }
    ]);
  };

  const updateTimelineNote = (id: string, field: "time" | "note", value: string) => {
    setTimelineNotes(
      timelineNotes.map((n) => (n.id === id ? { ...n, [field]: value } : n))
    );
  };

  const removeTimelineNote = (id: string) => {
    setTimelineNotes(timelineNotes.filter((n) => n.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Store structured data as JSON
      const structuredNotes = JSON.stringify({
        timelineNotes: timelineNotes.filter(n => n.time || n.note),
        specialInstructions,
        hotelDetails,
        groundTransport,
        mealArrangements,
        generalNotes: additionalNotes,
      });

      const { error } = await supabase
        .from("inquiry_contracts")
        .update({
          additional_notes: structuredNotes,
          updated_at: new Date().toISOString(),
        })
        .eq("id", document.id);

      if (error) throw error;

      toast.success("Itinerary details saved");
      logActivity({ 
        inquiryId: document.inquiry_id, 
        action: 'itinerary_saved', 
        description: `Itinerary ${document.contract_number} details updated`,
        metadata: { documentId: document.id, contractNumber: document.contract_number }
      });
      onSaved?.();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const origin = document.event_details?.origin || "—";
  const docType = document.event_details?.type || "travel_itinerary";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plane className="h-5 w-5 text-primary" />
            Edit Travel Itinerary
          </DialogTitle>
          <DialogDescription className="flex items-center gap-3">
            <Badge variant="secondary">{document.contract_number}</Badge>
            <span>Origin: {origin}</span>
            <span className="text-muted-foreground">
              Created {format(new Date(document.created_at), "MMM d, yyyy")}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Hotel Details */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Hotel & Accommodation
            </Label>
            <Textarea
              value={hotelDetails}
              onChange={(e) => setHotelDetails(e.target.value)}
              placeholder="Hotel name, address, confirmation number, check-in/check-out times..."
              rows={3}
            />
          </div>

          {/* Ground Transport */}
          <div className="space-y-2">
            <Label>Ground Transportation</Label>
            <Textarea
              value={groundTransport}
              onChange={(e) => setGroundTransport(e.target.value)}
              placeholder="Car rental details, driver contact, pickup instructions..."
              rows={2}
            />
          </div>

          {/* Meal Arrangements */}
          <div className="space-y-2">
            <Label>Meal Arrangements</Label>
            <Textarea
              value={mealArrangements}
              onChange={(e) => setMealArrangements(e.target.value)}
              placeholder="Meal vouchers, per diem, restaurant recommendations..."
              rows={2}
            />
          </div>

          {/* Timeline Notes */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Additional Timeline Notes
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addTimelineNote}
              >
                <Plus className="h-3 w-3 mr-1" /> Add Note
              </Button>
            </div>
            
            {timelineNotes.length > 0 && (
              <div className="space-y-2">
                {timelineNotes.map((note) => (
                  <div key={note.id} className="flex items-start gap-2">
                    <Input
                      value={note.time}
                      onChange={(e) => updateTimelineNote(note.id, "time", e.target.value)}
                      placeholder="Time (e.g., 3:00 PM)"
                      className="w-28"
                    />
                    <Input
                      value={note.note}
                      onChange={(e) => updateTimelineNote(note.id, "note", e.target.value)}
                      placeholder="Note (e.g., Meet coordinator at lobby)"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTimelineNote(note.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Special Instructions */}
          <div className="space-y-2">
            <Label>Special Instructions</Label>
            <Textarea
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              placeholder="Dress code, equipment notes, contact persons, emergency info..."
              rows={3}
            />
          </div>

          {/* General Notes */}
          <div className="space-y-2">
            <Label>Additional Notes</Label>
            <Textarea
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              placeholder="Any other details..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Details
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
